// apps/merchant-v3/src/components/scope/ScopeGate.tsx
//
// Phase 6.1 Wave 5 Task 24 — route-level scope gate for the KPI-bearing
// read surfaces (dashboard / orders / products). When GET /auth/me/scope
// reports a no-team member (isAllScope=false, allowedStoreIds=[], teamId=
// null — D-12 fail-closed), we render the dedicated NoTeamEmptyState in
// place of the page. Because the whole page body is swapped, the KPI cards
// are hidden entirely rather than rendered as "0" (P6.1.D-28b, G-12).
//
// Owner/admin (isAllScope=true) and team members (populated scope) fall
// straight through to the real page. While scope is loading we show the
// LoadingShell so the empty state never flashes for a scoped user
// mid-bootstrap (loading skeleton per D-28c).
//
// vg-binding: PLAN:task-24
// vg-binding: P6.1.D-28
// vg-binding: P6.1.D-02
// vg-binding: G-12
// vg-binding: G-36
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape

import type { JSX, ReactNode } from 'react';

import { LoadingShell } from '@/components/LoadingShell';
import { NoTeamEmptyState } from '@/components/scope/NoTeamEmptyState';
import { useMeScope } from '@/lib/scope/useMeScope';

interface ScopeGateProps {
  children: ReactNode;
}

export function ScopeGate({ children }: ScopeGateProps): JSX.Element {
  const { isLoading, isNoTeamMember } = useMeScope();

  // Defer render until scope resolves — never flash the empty state for a
  // scoped user (or the KPI page for a no-team member) mid-bootstrap.
  if (isLoading) {
    return <LoadingShell />;
  }

  if (isNoTeamMember) {
    return <NoTeamEmptyState />;
  }

  return <>{children}</>;
}

export default ScopeGate;
